<template>
  <oms-bulk-action-modal
    id="adjustOrdersModal"
    :title="t('features.vue.adjust_orders_modal.title')"
    :filters="filters"
    :params="params"
    :total-results="totalResults"
    :allow-empty-selection="hasGoogleSheetAction"
  >

    <!-- Divider -->
    <hr class="my-4" />

    <div class="d-flex align-items-center mt-2">
      <input
        type="checkbox"
        v-model="cancelWithoutNotifyCustomer"
        class="mb-2"
        id="cancelWithoutNotifyCheckbox"
      />
      <label for="cancelWithoutNotifyCheckbox" class="ml-2 h5">
        {{ t('features.vue.adjust_orders_modal.cancel_without_notify') }}
      </label>
    </div>

    <div class="d-flex align-items-center mt-2">
      <input
        type="checkbox"
        v-model="exportToCustomerPortal"
        class="mb-2"
        id="exportToCustomerPortalCheckbox"
      />
      <label for="exportToCustomerPortalCheckbox" class="ml-2 h5">
        {{ t('features.vue.adjust_orders_modal.resync_boxes') }}
      </label>
    </div>

    <!-- Divider -->
    <hr class="my-4" />

    <!-- CHANGE STATUS SECTION -->
    <h5>{{ t('features.vue.adjust_orders_modal.change_status') }}</h5>
    <div class="mt-2 d-flex flex-column">
      <label>{{ t('features.vue.adjust_orders_modal.status') }}</label>
      <multiselect
        v-model="selectedStatus"
        :options="[statuses.processing]"
        :multiple="false"
        :taggable="false"
        :placeholder="t('features.vue.adjust_orders_modal.select_status')"
      />
      <div class="mt-3 alert alert-warning">
        {{ t('features.vue.adjust_orders_modal.change_status_warning') }}
      </div>
    </div>

    <!-- Divider -->
    <hr class="my-4" />

    <!-- TAGS SECTION -->
    <h5 class="mt-2">{{ t('features.vue.adjust_orders_modal.tags') }}</h5>
    <div class="mt-2 d-flex flex-column gap-3">
      <div class="d-flex flex-column">
        <label>{{ t('features.vue.adjust_orders_modal.add_tags_from_google_sheet') }}</label>
        <input
          type="text"
          v-model="googleSheetLink"
          class="form-control mb-2"
          :placeholder="t('features.vue.adjust_orders_modal.google_sheet_link_placeholder')"
        />
        <input
          type="text"
          v-model="googleSheetName"
          class="form-control mb-2"
          :placeholder="t('features.vue.adjust_orders_modal.google_sheet_name_placeholder')"
        />
        <multiselect
          v-model="googleSheetTagsToAdd"
          :options="currentTags.concat(newTags)"
          :multiple="true"
          :show-labels="false"
          :taggable="true"
          @tag="addTag(googleSheetTagsToAdd, $event)"
        />
      </div>

      <div class="mt-3 alert alert-warning">
        {{ t('features.vue.adjust_orders_modal.google_sheet_warning') }}
      </div>

      <div class="d-flex flex-column">
        <label>{{ t('features.vue.adjust_orders_modal.add_tags') }}</label>
        <multiselect
          v-model="tagsToAdd"
          :options="currentTags.concat(newTags)"
          :multiple="true"
          :show-labels="false"
          :taggable="true"
          @tag="addTag(tagsToAdd, $event)"
        />
      </div>

      <div class="d-flex flex-column">
        <label>{{ t('features.vue.adjust_orders_modal.remove_tags') }}</label>
        <multiselect
          v-model="tagsToRemove"
          :options="currentTags.concat(newTags)"
          :multiple="true"
          :show-labels="false"
          :taggable="true"
          @tag="addTag(tagsToRemove, $event)"
        />
      </div>
    </div>

    <!-- Divider -->
    <hr class="my-4" />

    <!-- ADDONS SECTION -->
    <h5>{{ t('features.vue.adjust_orders_modal.addons') }}</h5>
    <div class="mt-2 d-flex flex-column gap-3">
      <div class="d-flex flex-column">
        <label>{{ t('features.vue.adjust_orders_modal.add_addons') }}</label>
        <multiselect
          v-model="addonsToAdd"
          :options="parsedAddons"
          :multiple="true"
          :taggable="false"
          :custom-label="option => option.label"
          track-by="id"
        />
      </div>

      <div class="d-flex flex-column">
        <label>{{ t('features.vue.adjust_orders_modal.remove_addons') }}</label>
        <multiselect
          v-model="addonsToRemove"
          :options="parsedAddons"
          :multiple="true"
          :taggable="false"
          :custom-label="option => option.label"
          track-by="id"
        />
      </div>
    </div>

    <!-- Divider -->
    <hr class="my-4" />

    <!-- ADDON GROUPS SECTION -->
    <h5>{{ t('features.vue.adjust_orders_modal.addon_groups') }}</h5>
    <div class="mt-2 d-flex flex-column gap-3">
      <div class="d-flex flex-column">
        <label>{{ t('features.vue.adjust_orders_modal.add_addon_groups') }}</label>
        <multiselect
          v-model="addonGroupsToAdd"
          :options="parsedAddonGroups"
          :multiple="true"
          :taggable="false"
          :custom-label="option => option.label"
          track-by="id"
        />
      </div>

      <div class="d-flex flex-column">
        <label>{{ t('features.vue.adjust_orders_modal.remove_addon_groups') }}</label>
        <multiselect
          v-model="addonGroupsToRemove"
          :options="parsedAddonGroups"
          :multiple="true"
          :taggable="false"
          :custom-label="option => option.label"
          track-by="id"
        />
      </div>
    </div>

    <div class="mt-3 alert alert-warning">
      {{ t('features.vue.adjust_orders_modal.addon_groups_warning') }}
    </div>

    <!-- Divider -->
    <hr class="my-4" />

    <!-- HOLD ORDERS SECTION -->
    <h5>{{ t('features.vue.adjust_orders_modal.hold_orders') }}</h5>
    <div class="mt-2 d-flex flex-column">
      <label>{{ t('features.vue.adjust_orders_modal.reason') }}</label>
      <multiselect
        v-model="selectedReason"
        :options="holdStatusReasons"
        :group-select="true"
        group-label="status"
        group-values="reasons"
        :show-labels="false"
      />
    </div>

    <div class="mt-3 alert alert-warning">
      {{ t('features.vue.adjust_orders_modal.hold_orders_warning') }}
    </div>
  </oms-bulk-action-modal>
</template>

<script>
import Multiselect from 'vue-multiselect';
import OmsBulkActionModal from './bulk-action-modal.vue';

export default {
  inject: ['i18n'],

  props: {
    filters: Object,
    holdStatusReasons: Array,
    currentTags: Array,
    addons: Object,
    addon_groups: Object,
    statuses: Object,
    totalResults: Number
  },

  components: { Multiselect, OmsBulkActionModal },

  data() {
    return {
      selectedReason: undefined,
      selectedStatus: undefined,
      cancelWithoutNotifyCustomer: false,
      exportToCustomerPortal: false,
      googleSheetLink: '',
      googleSheetName: '',
      googleSheetTagsToAdd: [],
      tagsToAdd: [],
      tagsToRemove: [],
      newTags: [],
      addonsToAdd: [],
      addonsToRemove: [],
      addonGroupsToAdd: [],
      addonGroupsToRemove: []
    };
  },

  computed: {
    parsedAddons() {
      return Object.entries(this.addons).map(([id, label]) => ({
        id: parseInt(id),
        label
      }));
    },

     parsedAddonGroups() {
      return Object.entries(this.addon_groups).map(([id, label]) => ({
        id: parseInt(id),
        label
      }));
    },

    hasGoogleSheetAction() {
      return this.googleSheetLink.length > 0 && this.googleSheetTagsToAdd.length > 0;
    },

    params() {
      const actions = [];

      if (this.googleSheetLink && this.googleSheetTagsToAdd.length) {
        actions.push({
          type: 'adjust_tags_from_google_sheet',
          payload: {
            google_sheet_link: this.googleSheetLink,
            google_sheet_name: this.googleSheetName,
            add: this.googleSheetTagsToAdd
          }
        });
      }

      if (this.tagsToAdd.length || this.tagsToRemove.length) {
        actions.push({
          type: 'adjust_tags',
          payload: {
            add: this.tagsToAdd,
            remove: this.tagsToRemove
          }
        });
      }

      if (this.addonsToAdd.length || this.addonsToRemove.length) {
        actions.push({
          type: 'adjust_addons',
          payload: {
            add: this.addonsToAdd.map(a => a.id),
            remove: this.addonsToRemove.map(a => a.id)
          }
        });
      }

      if (this.addonGroupsToAdd.length || this.addonGroupsToRemove.length) {
        actions.push({
          type: 'adjust_addon_groups',
          payload: {
            add: this.addonGroupsToAdd.map(a => a.id),
            remove: this.addonGroupsToRemove.map(a => a.id)
          }
        });
      }

      if (this.selectedReason) {
        const status = this.holdStatusReasons.find(({ reasons }) =>
          reasons.includes(this.selectedReason)
        )?.status;

        if (status) {
          actions.push({
            type: status,
            payload: { reason: this.selectedReason }
          });
        }
      }

      if (this.selectedStatus) {
        actions.push({
          type: 'change_status',
          payload: {
            status: this.selectedStatus
          }
        });
      }

      if (this.cancelWithoutNotifyCustomer) {
        actions.push({
          type: 'cancel_without_notify_customer',
          payload: {
            status: this.cancelWithoutNotifyCustomer
          }
        });
      }

      if (this.exportToCustomerPortal) {
        actions.push({
          type: 'export_to_customer_portal',
          payload: {}
        });
      }

      return { actions };
    }
  },

  methods: {
    t(key, options) {
      return this.i18n.t(key, options)
    },

    addTag(list, tag) {
      list.push(tag);
      this.newTags.push(tag);
    }
  }
};
</script>